import React, { useState, useEffect } from "react";
import cn from "classnames";
import styles from "./Hero.module.sass";
import History from "./History";
import Item from "./Item";
import ScrollParallax from "../../../components/ScrollParallax";
import { useCategories, useArticles } from "../../../hooks/useArticles";
import { formatArticleForDisplay } from "../../../utils/formatters";


const Hero = () => {
  const [activeIndex, setActiveIndex] = useState(0);
  const [articlesByCategory, setArticlesByCategory] = useState({});
  const [loadingMore, setLoadingMore] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [isLoading, setIsLoading] = useState(false); // Nouveau state pour éviter les requêtes multiples

  // Utilisation des hooks personnalisés
  const { categories, loading: categoriesLoading, fetchCategories } = useCategories();
  const { fetchArticles, fetchArticlesByCategory } = useArticles();

  // Charger les catégories et articles au montage du composant
  useEffect(() => {
    loadCategoriesAndArticles();
  }, []);

  const loadCategoriesAndArticles = async () => {
    // Éviter les requêtes multiples simultanées
    if (isLoading) {
      console.log('Chargement déjà en cours, annulation...');
      return;
    }

    try {
      setIsLoading(true);
      console.log('Chargement des catégories et articles...');
      console.log('URL PocketBase:', process.env.REACT_APP_POCKETBASE_URL);
      
      // Récupérer toutes les catégories
      const categoriesData = await fetchCategories();
      console.log('Catégories récupérées:', categoriesData);
      
      if (categoriesData.length === 0) {
        console.log('Aucune catégorie trouvée, utilisation des données statiques');
        return;
      }

      // APPROCHE OPTIMISÉE : Charger les articles par catégorie (plus rapide)
      console.log('Chargement des articles par catégorie...');
      const articlesData = {};
      
      // Charger les articles pour chaque catégorie en parallèle
      const categoryPromises = categoriesData.map(async (category) => {
        try {
          console.log(`Chargement des articles pour la catégorie: ${category.name}`);
          const result = await fetchArticlesByCategory(category.id, {
            page: 1,
            perPage: 8, // Réduire le nombre pour accélérer
            sort: '-created'
          });
          
          console.log(`Articles récupérés pour ${category.name}:`, result.items.length);
          
          // Formater les articles pour l'affichage
          const formattedItems = result.items
            .map(article => {
              try {
                return formatArticleForDisplay(article);
              } catch (error) {
                console.error('Erreur lors du formatage de l\'article:', error, article);
                return null;
              }
            })
            .filter(item => item !== null);
          
          return {
            categoryId: category.id,
            data: {
              title: category.name,
              items: formattedItems.slice(0, 6), // Limiter à 6 pour l'affichage
              totalItems: result.totalItems,
              hasMore: result.totalItems > 6
            }
          };
        } catch (error) {
          console.error(`Erreur lors du chargement des articles pour ${category.name}:`, error);
          return {
            categoryId: category.id,
            data: {
              title: category.name,
              items: [],
              totalItems: 0,
              hasMore: false
            }
          };
        }
      });

      // Attendre que toutes les catégories soient chargées
      const categoryResults = await Promise.all(categoryPromises);
      
      // Construire l'objet final
      categoryResults.forEach(({ categoryId, data }) => {
        articlesData[categoryId] = data;
      });

      setArticlesByCategory(articlesData);
      console.log('Données des articles par catégorie:', articlesData);
      
    } catch (error) {
      console.error('Erreur lors du chargement des données:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const loadMoreArticles = async () => {
    if (loadingMore || categories.length === 0) return;

    const currentCategory = categories[activeIndex];
    const currentCategoryData = articlesByCategory[currentCategory.id];
    
    if (!currentCategoryData?.hasMore) return;

    try {
      setLoadingMore(true);
      const nextPage = currentPage + 1;
      
      // Charger plus d'articles pour cette catégorie spécifique
      const result = await fetchArticlesByCategory(currentCategory.id, {
        page: nextPage,
        perPage: 6,
        sort: '-created'
      });

      // Formater et filtrer les nouveaux articles pour éviter les doublons
      const newFormattedArticles = result.items
        .map(article => formatArticleForDisplay(article))
        .filter(formattedArticle => {
          // Vérifier que l'article n'existe pas déjà dans la catégorie
          return !currentCategoryData.items.some(existingItem => existingItem.id === formattedArticle.id);
        });

      setArticlesByCategory(prev => ({
        ...prev,
        [currentCategory.id]: {
          ...prev[currentCategory.id],
          items: [...prev[currentCategory.id].items, ...newFormattedArticles],
          hasMore: result.totalItems > (prev[currentCategory.id].items.length + newFormattedArticles.length)
        }
      }));

      setCurrentPage(nextPage);
      console.log(`${newFormattedArticles.length} nouveaux articles chargés pour ${currentCategory.name}`);
    } catch (error) {
      console.error('Erreur lors du chargement de plus d\'articles:', error);
    } finally {
      setLoadingMore(false);
    }
  };

  // Préparer les données d'affichage depuis PocketBase
  const displayData = categories.length > 0 && Object.keys(articlesByCategory).length > 0 
    ? categories.map(cat => ({
        title: cat.name,
        items: articlesByCategory[cat.id]?.items || [],
        hasMore: articlesByCategory[cat.id]?.hasMore || false,
        totalItems: articlesByCategory[cat.id]?.totalItems || 0
      }))
    : []; // Tableau vide si pas de données PocketBase


  // Réinitialiser la page quand on change de catégorie
  useEffect(() => {
    setCurrentPage(1);
  }, [activeIndex]);

  // Forcer le re-calcul des animations ScrollParallax quand les données changent
  useEffect(() => {
    if (displayData[activeIndex]?.items?.length > 0) {
      // Petit délai pour s'assurer que le DOM est mis à jour
      setTimeout(() => {
        // Déclencher un événement de scroll pour réactiver les animations
        window.dispatchEvent(new Event('scroll'));
      }, 100);
    }
  }, [displayData, activeIndex]);

  if (categoriesLoading || isLoading) {
    return (
      <div className={cn("section", styles.section)}>
        <div className={cn("container", styles.container)}>
          <h2 className={cn("hero", styles.title)}>Actualités</h2>
          <div className={styles.info}>
            {categoriesLoading ? 'Chargement des catégories...' : 'Chargement des articles...'}
          </div>
          {/* Skeleton loader pour une meilleure UX */}
          <div style={{ marginTop: '40px' }}>
            <div style={{ 
              display: 'flex', 
              gap: '12px', 
              marginBottom: '30px',
              flexWrap: 'wrap'
            }}>
              {[1,2,3,4].map(i => (
                <div key={i} style={{
                  width: '120px',
                  height: '40px',
                  backgroundColor: '#f0f0f0',
                  borderRadius: '20px',
                  animation: 'pulse 1.5s ease-in-out infinite'
                }} />
              ))}
            </div>
            <div style={{ 
              display: 'grid', 
              gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', 
              gap: '20px' 
            }}>
              {[1,2,3,4,5,6].map(i => (
                <div key={i} style={{
                  height: '200px',
                  backgroundColor: '#f0f0f0',
                  borderRadius: '12px',
                  animation: 'pulse 1.5s ease-in-out infinite'
                }} />
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={cn("section", styles.section)}>
      <div className={cn("container", styles.container)}>
        <h2 className={cn("hero", styles.title)}>Actualités</h2>
        <div className={styles.info}>
          Suivez notre actualité pour rester informé de nos dernières actualités.
        </div>

        <History />
        {displayData.length > 0 && (
          <div className={styles.nav}>
            {displayData.map((x, index) => (
              <button
                className={cn(styles.btn, {
                  [styles.active]: index === activeIndex,
                })}
                onClick={() => setActiveIndex(index)}
                key={index}
              >
                {x.title}
              </button>
            ))}
          </div>
        )}

        <div className={styles.list}>
          {displayData[activeIndex]?.items?.length > 0 ? (
            displayData[activeIndex].items.map((x, index) => (
              <div 
                className={styles.box} 
                key={`${activeIndex}-${index}`}
                style={{ 
                  opacity: 1, 
                  transform: 'translateY(0px)',
                  transition: `all 0.6s ease ${index * 0.1}s`
                }}
              >
                <Item item={x} className={styles.item} />
              </div>
            ))
          ) : (
            <div style={{ 
              padding: '60px 20px', 
              textAlign: 'center', 
              color: '#666',
              width: '100%',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              minHeight: '200px',
              fontSize: '16px'
            }}>
              {categoriesLoading || isLoading 
                ? 'Chargement des articles...' 
                : displayData.length === 0 
                  ? 'Aucune catégorie trouvée. Vérifiez votre connexion PocketBase.'
                  : 'Aucun article trouvé pour cette catégorie'
              }
            </div>
          )}
        </div>
        {displayData[activeIndex]?.hasMore && (
          <div className={styles.btns}>
            <button 
              className={cn("button-stroke button-small", styles.button)}
              onClick={loadMoreArticles}
              disabled={loadingMore}
            >
              {loadingMore ? 'Chargement...' : 'Voir plus'}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default Hero;
